import { reportOnce } from '@protector/api-client';
import type { Dictionary, I18nMap, UUID } from '@protector/utils/types';
import { groupBy, keyBy } from 'lodash';
import { createSelector } from 'reselect';
import type { AppState, Selector } from '../../__legacy__/redux.types.ts';
import {
  getCurrentAreaId,
  getCurrentSeasonFields,
  getPropertyEntitiesInEntities,
  getSelectedPropertyId,
  getSelectedSeasons
} from '../../core/__legacy__/redux.selectors.ts';
import type { Variety } from '../../crop/models.ts';
import { getSelectedProperty, selectPropertyEntities } from '../../property/__legacy__/redux.selectors.ts';
import { RegionType, type AreaVariableMassiveStages } from '../../region/constants.ts';
import type { AreaVariable, Region, SeasonArea } from '../../region/models.ts';
import { getRootRegion, sortByName } from '../../region/utils.ts';
import { entity } from './redux.reducer.ts';

const reportMissingRegionSeasons = (site: string) => {
  reportOnce(
    `region.__legacy__.redux.selectors:${site}`,
    () => new Error(`region.__legacy__.redux.selectors: region.seasons missing -- ${site} fell back to []`),
    { errorType: 'StateFallback', domain: 'Region' }
  );
};

const { selectEntities, selectAll } = entity.getSelectors();

export const selectRegionEntities = (state: AppState) => selectEntities(state.entities.region);

export const selectAllRegions = createSelector(
  (state: AppState) => selectAll(state.entities.region),
  regions => [...regions].sort(sortByName)
);

export type GetAreaVariablesPerSeasonAreaReturn = Record<string, Record<string, AreaVariable>>;

export interface AreaVariableNameAndKey {
  key: string;
  name: I18nMap;
}

export const getAreaVariableMassiveStage: Selector<AreaVariableMassiveStages | undefined> = state =>
  state.entities.region.areaVariableLoadingStage;

export const getParentRegion = createSelector(selectAllRegions, regions => regions.find(region => !region.parent_id));

export const getRecursiveRegionsAndSeasonArea = (state: AppState) => state.entities.region.recursiveRegionsAndSeasonFields;

export const getSeasonAreaMap: Selector<Record<string, SeasonArea>> = state => {
  const { seasonAreas } = state.entities.region;
  let seasonAreaMap: Record<string, SeasonArea> = {};
  seasonAreas?.forEach(element => {
    seasonAreaMap = { ...seasonAreaMap, [element.seasonAreaId]: element };
  });
  return { ...seasonAreaMap };
};

export const getAreaVariablesPerSeasonArea: Selector<GetAreaVariablesPerSeasonAreaReturn | undefined> = state => {
  const mapSeasonAreaIdToAreaVariables: GetAreaVariablesPerSeasonAreaReturn = {};

  const areaVariablesPerSeason = state.entities.region.areaVariablesPerSeason;

  if (!areaVariablesPerSeason) return;

  Object.values(areaVariablesPerSeason).forEach(areaVariables => {
    areaVariables.forEach(areaVariable => {
      if (areaVariable?.season_area_id && !mapSeasonAreaIdToAreaVariables?.[areaVariable.season_area_id]) {
        mapSeasonAreaIdToAreaVariables[areaVariable.season_area_id] = {};
      }
      mapSeasonAreaIdToAreaVariables[areaVariable.season_area_id][areaVariable.key] = areaVariable;
    });
  });
  return mapSeasonAreaIdToAreaVariables;
};

export const getAvailableAreaVariableNamesAndKeys: Selector<AreaVariableNameAndKey[]> = state => {
  let areaVariablesNamesAndKeys: AreaVariableNameAndKey[] = [];
  const { areaVariablesPerSeason } = state.entities.region;
  Object.values(areaVariablesPerSeason).forEach(areaVariables => {
    areaVariables.forEach(areaVariable => {
      const { key, name } = areaVariable;
      if (!areaVariablesNamesAndKeys.some(el => el.key === key)) {
        areaVariablesNamesAndKeys = [...areaVariablesNamesAndKeys, { name, key }];
      }
    });
  });
  return areaVariablesNamesAndKeys;
};

export const isTableInfoLoading: Selector<boolean> = state => state.entities.region.isTableInfoLoading;

export const getAreaVariables: Selector<AreaVariable[]> = state => {
  return Object.values(state.entities.region.areaVariablesPerSeason).flat();
};

export const isRegionsLoaded: Selector<boolean> = state => state.entities.region.isRegionsLoaded;

export const getSeasonFieldsCropsVarieties: Selector<Record<string, Variety[]> | null> = state =>
  state.entities.region.seasonFieldsCropsVarieties;

export const getSeverityIndicatorsSelected: Selector<UUID | undefined> = state => state.entities.region.severityIndicatorSelected;

export const getHasPressureIndicator: Selector<boolean | undefined> = state => state.entities.region.hasPressureIndicator;

const regionsToDictionary = (regions: Region[]): Dictionary<Region> => {
  const regionsMap: Dictionary<Region> = {};
  regions.forEach(region => {
    regionsMap[region.id] = region;
  });
  return regionsMap;
};

export const safelySelectRegions = createSelector(
  selectAllRegions,
  getCurrentSeasonFields,
  getSelectedSeasons,
  (regions, currentSeasonArea, selectedSeasons) => {
    if (!regions) return [];

    const dictSeasonAreas = groupBy(currentSeasonArea, 'areaId');

    return regions.map(region => {
      return {
        ...region,
        current_info: region.current_info && {
          ...region.current_info,
          crop_ended: !dictSeasonAreas[region.id]?.some(seasonArea => selectedSeasons?.includes(seasonArea.seasonId))
        }
      };
    });
  }
);

export const safelySelectRegionsDictionary = createSelector(safelySelectRegions, regionsToDictionary);

export const safelySelectRegionsInCurrentSeason = createSelector(safelySelectRegions, getSelectedSeasons, (regions, seasons) => {
  return regions.filter(region => {
    const seasonIds = region.seasons.map(s => s.id);
    return seasonIds.some(id => seasons.includes(id));
  });
});

export const safelySelectRegionsInCurrentSeasonDictionary = createSelector(safelySelectRegionsInCurrentSeason, regionsToDictionary);

export const selectSafelyCurrentSeasonFields = createSelector(safelySelectRegionsInCurrentSeason, regions =>
  regions.filter(region => region.type === RegionType.AREA)
);

export const selectSafelyCurrentSeasonFieldsIds = createSelector(selectSafelyCurrentSeasonFields, regions =>
  regions.map(region => region.id)
);

export const selectRegion = createSelector(selectRegionEntities, regions => (regionId: UUID) => regions[regionId]);

export const selectRootRegion = createSelector(
  selectAllRegions,
  selectPropertyEntities,
  getSelectedPropertyId,
  (regions, properties, selectedProperty) =>
    selectedProperty && properties[selectedProperty] ? getRootRegion(regions, properties, selectedProperty) : null
);

export const selectRootRegionByCropStatus = createSelector(
  [
    selectAllRegions,
    getPropertyEntitiesInEntities,
    getSelectedPropertyId,
    (_, showFieldsCropEnded: boolean) => showFieldsCropEnded,
    (_, __, showFieldsCropFuture: boolean) => showFieldsCropFuture,
    (state: AppState) => state.entities.region.currentInfoCalculated,
    (_state: AppState, _showFieldsCropEnded: boolean, _showFieldsCropFuture: boolean, cropStatusIsKnownOverride?: boolean) =>
      cropStatusIsKnownOverride
  ],
  (regions, properties, selectedProperty, showFieldsCropEnded, showFieldsCropFuture, currentInfoCalculated, cropStatusIsKnownOverride) => {
    return selectedProperty && properties[selectedProperty]
      ? getRootRegion(
          regions,
          properties,
          selectedProperty,
          showFieldsCropEnded,
          showFieldsCropFuture,
          true,
          cropStatusIsKnownOverride ?? currentInfoCalculated
        )
      : null;
  }
);

export const selectRootRegionByArchivedFields = createSelector(
  [selectAllRegions, selectPropertyEntities, getSelectedPropertyId, (_: AppState, showArchivedFields: boolean) => showArchivedFields],
  (regions, properties, selectedProperty, showArchivedFields) =>
    selectedProperty && properties[selectedProperty]
      ? getRootRegion(regions, properties, selectedProperty, true, true, showArchivedFields)
      : null
);

export const selectRegionDictionaryByCurrentSeason = createSelector(
  selectAllRegions,
  (state: AppState) => state.uiState.global.selectedSeasons,
  (regions, seasons) => {
    return keyBy(
      regions.filter(r => {
        if (!r.seasons) reportMissingRegionSeasons('selectRegionDictionaryByCurrentSeason');
        const seasonIds = r.seasons?.map(s => s.id) ?? [];
        return seasonIds.some(id => seasons.includes(id));
      }),
      'id'
    );
  }
);

export const selectRegionByCurrentSeason = createSelector(
  selectAllRegions,
  (state: AppState) => state.uiState.global.selectedSeasons,
  (regions, seasons) => {
    return regions.filter(r => {
      if (!r.seasons) reportMissingRegionSeasons('selectRegionByCurrentSeason');
      const seasonIds = r.seasons?.map(s => s.id) ?? [];
      return seasonIds.some(id => seasons.includes(id));
    });
  }
);

export const selectRegionByProperty = createSelector(
  getSelectedProperty,
  (state: AppState) => state.entities.region.entities,
  (property, regions) => {
    return property ? regions[property.root_region_id] : undefined;
  }
);

export const selectAreasByProperty = createSelector(
  getSelectedProperty,
  selectRegionEntities,
  selectAllRegions,
  (property, regionsById, regions) => {
    function getAreas(region_parent: Region, grouped_by_parent: Dictionary<Region[]>): Region[] {
      const children = grouped_by_parent[region_parent.id];

      return children
        ? children.reduce((acc: Region[], item) => {
            if (item.type === RegionType.AREA) {
              acc.push(item);
            } else {
              acc.push(...getAreas(item, grouped_by_parent));
            }
            return acc;
          }, [])
        : [];
    }

    const region = property && regionsById[property.root_region_id];

    if (!region) return;

    const grouped_regions = groupBy(regions, 'parent_id');

    return getAreas(region, grouped_regions);
  }
);

export const getCurrentArea = createSelector(
  (state: AppState) => state,
  getCurrentAreaId,
  (state, selectedAreaId) => {
    if (!selectedAreaId) return null;
    return selectRegion(state)(selectedAreaId) ?? null;
  }
);
